import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Shield, Key, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { useAdminAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';

export const AdminLoginPage: React.FC = () => {
  const { login, isLoading, error } = useAdminAuth();
  const { isAdminAuthenticated, hasHydrated } = useAuthStore();
  const [token, setToken] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  // Wait for hydration before checking auth
  if (!hasHydrated) {
    return null;
  }

  // Redirect if already authenticated
  if (isAdminAuthenticated) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!token.trim()) {
      setLocalError('Por favor, insira o token de acesso');
      return;
    }

    try {
      await login(token.trim());
    } catch {
      setLocalError(error?.message || 'Token invalido ou expirado');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 dark:from-secondary-900 dark:to-secondary-800 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            Admin
          </span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Warning banner */}
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800 dark:text-yellow-200 text-sm">
                  Area Restrita
                </h3>
                <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                  Este acesso e exclusivo para administradores autorizados.
                  Tentativas de acesso nao autorizadas serao registradas.
                </p>
              </div>
            </div>
          </div>

          <Card className="shadow-xl">
            <CardContent>
              {/* Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <Key className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
                Acesso Administrativo
              </h1>
              <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
                Insira seu token de administrador para acessar o painel
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Token input */}
                <div>
                  <label
                    htmlFor="token"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    Token de Acesso
                  </label>
                  <input
                    type="password"
                    id="token"
                    value={token}
                    onChange={(e) => {
                      setToken(e.target.value);
                      setLocalError(null);
                    }}
                    placeholder="Digite seu token de admin"
                    className="input font-mono"
                    autoComplete="off"
                    autoFocus
                  />
                </div>

                {/* Error message */}
                {(localError || error) && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {localError || error?.message}
                    </p>
                  </div>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  variant="danger"
                  fullWidth
                  size="lg"
                  isLoading={isLoading}
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Acessar Painel
                </Button>
              </form>

              {/* Info */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-secondary-700">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-gray-400 mt-0.5" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Todas as acoes administrativas sao registradas com IP e
                    timestamp para fins de auditoria.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Back to user portal link */}
          <p className="text-center mt-6 text-sm text-gray-600 dark:text-gray-400">
            <a
              href="/auth"
              className="inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:underline font-medium"
            >
              <img src="/logo.png" alt="KeepNota" className="h-4 w-auto" />
              Voltar ao Portal do Usuario
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          KeepNota Admin Panel - Acesso Restrito
        </p>
      </footer>
    </div>
  );
};

export default AdminLoginPage;
